// Retry policy for the lead relay (docs/lead-relay.md §7.4). Pure, no clock.

import type { LeadAttempt, LeadFailureReason } from '../../types/leadRelay.js';

/** Total attempts in one sequence (a Resend starts a new one). */
export const MAX_ATTEMPTS = 5;

/** Minutes to wait AFTER attempt n (1-based) before attempt n+1. */
export const BACKOFF_MINUTES: readonly number[] = [5, 10, 20, 40];

/**
 * Reasons that cannot succeed by waiting — a missing field stays missing, a
 * route that went missing stays missing. These go straight to `failed`.
 */
export const NON_RETRYABLE: ReadonlySet<LeadFailureReason> = new Set<LeadFailureReason>([
  'missing_required',
  'no_route',
  'blocked_default_interest',
  'invalid_route',
  // Bot protection refused US; resending into a block changes nothing.
  'provider_blocked',
]);

export type RetryDecision =
  | { kind: 'retry'; nextAttemptAt: Date }
  | { kind: 'give_up' };

/**
 * After a failed attempt: retry with backoff, or give up once the sequence has
 * used MAX_ATTEMPTS. `attemptsInSequence` counts the attempt just made.
 */
export function decideRetry(
  attemptsInSequence: number,
  reason: LeadFailureReason,
  now: Date,
): RetryDecision {
  if (NON_RETRYABLE.has(reason)) return { kind: 'give_up' };
  if (attemptsInSequence >= MAX_ATTEMPTS) return { kind: 'give_up' };
  const minutes =
    BACKOFF_MINUTES[attemptsInSequence - 1] ?? BACKOFF_MINUTES[BACKOFF_MINUTES.length - 1]!;
  return { kind: 'retry', nextAttemptAt: new Date(now.getTime() + minutes * 60_000) };
}

/**
 * ⚠️⚠️ THE CEILING ON AN UNRECOGNISED RESPONSE (2026-10-07). An Imperva
 * bot-protection CHALLENGE parses as `provider_unrecognised_response` — a 2xx
 * that is not a created lead. Retrying it the ordinary five times answers a
 * challenge with more of the traffic that triggered it, escalating the very
 * blocking we are reacting to: we would be hammering a client's vendor with the
 * client's own leads. So it gets ONE retry, then stops.
 *
 * Counted per SEQUENCE (a Resend starts a new one, deliberately — that is a
 * person's decision) and only over attempts that ended this way, so earlier
 * network failures in the same sequence do not use it up.
 */
export const UNRECOGNISED_RESPONSE_CEILING = 2;

/** True once this sequence has had UNRECOGNISED_RESPONSE_CEILING unrecognised responses. */
export function hitUnrecognisedCeiling(sequence: readonly Pick<LeadAttempt, 'reason'>[]): boolean {
  return sequence.filter((a) => a.reason === 'provider_unrecognised_response').length >= UNRECOGNISED_RESPONSE_CEILING;
}

/** What the lead and the alert say when the ceiling stopped the retries — never a silent give-up. */
export function unrecognisedCeilingMessage(system: string): string {
  return `Retries STOPPED after ${UNRECOGNISED_RESPONSE_CEILING} unrecognised responses from ${system} — on purpose: if it is bot protection challenging the relay, more requests make it worse. Check the stored response, key the lead in by hand, or Resend once the cause is known.`;
}
